import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, RefreshCw, AlertCircle, Car, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrafficPage = () => {
  const navigate = useNavigate();
  const [traffic, setTraffic] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchTraffic = async () => {
    try {
      const response = await axios.get(`${API}/traffic`);
      setTraffic(response.data);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching traffic:', error);
      toast.error('Failed to fetch traffic updates');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTraffic();
    const interval = setInterval(fetchTraffic, 5 * 60 * 1000); // Update every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'severity-high';
      case 'medium': return 'severity-medium';
      case 'low': return 'severity-low';
      default: return 'severity-none';
    }
  };

  return (
    <div className="traffic-page-container" data-testid="traffic-page">
      {/* Header */}
      <div className="traffic-page-header">
        <div className="header-left">
          <Button 
            onClick={() => navigate('/dashboard')} 
            variant="outline" 
            size="sm"
            className="back-btn"
            data-testid="back-button"
          >
            <ArrowLeft size={16} /> Back to Dashboard
          </Button>
        </div>
        
        <div className="header-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_presenter-toolbox/artifacts/xguxj4wx_KL1%20Logo-01.png" 
            alt="KL1 Radio Logo" 
            className="traffic-page-logo"
          />
          <div>
            <h1 className="page-title">Traffic & Travel</h1>
            <p className="page-subtitle">King's Lynn & West Norfolk</p>
          </div>
        </div>
        
        <div className="header-right">
          <Button 
            onClick={fetchTraffic} 
            variant="outline" 
            size="sm"
            className="refresh-btn"
            data-testid="refresh-traffic-button"
          >
            <RefreshCw size={16} /> Refresh
          </Button>
        </div>
      </div>

      <div className="traffic-page-content">
        {/* Waze Map */}
        <div className="traffic-map-section">
          <div className="section-header">
            <MapPin size={20} />
            <h2>Live Traffic Map - King's Lynn</h2>
          </div>
          <div className="waze-map-container">
            <iframe
              src="https://embed.waze.com/iframe?zoom=12&lat=52.7531&lon=0.3979&ct=livemap"
              width="100%"
              height="600"
              allowFullScreen
              title="Waze Live Traffic Map for King's Lynn"
            />
          </div>
          <p className="map-credit">Powered by Waze - Real-time traffic from fellow drivers</p>
        </div>

        {/* Traffic Incidents */}
        <div className="traffic-incidents-section">
          <div className="section-header">
            <AlertCircle size={20} />
            <h2>Current Traffic Incidents</h2>
            <span className="last-update">Updated: {lastUpdate.toLocaleTimeString()}</span>
          </div>
          
          <ScrollArea className="incidents-container">
            {loading ? (
              <p className="loading-text">Loading traffic information...</p>
            ) : traffic.length > 0 ? (
              <div className="incidents-list">
                {traffic.map((item, index) => (
                  <div 
                    key={`${item.severity}-${item.location}-${item.message}`}
                    className={`incident-card ${getSeverityColor(item.severity)}`}
                    data-testid={`incident-${index}`}
                  >
                    <div className="incident-header">
                      <div className="severity-badge">
                        <AlertCircle size={16} />
                        <span>{item.severity || 'Info'}</span>
                      </div>
                    </div>
                    <p className="incident-message">{item.message}</p>
                    {item.location && (
                      <div className="incident-location">
                        <MapPin size={14} />
                        <span>{item.location}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-incidents">
                <AlertCircle size={48} />
                <p>No traffic incidents reported</p>
                <p className="sub-text">All clear on the roads in West Norfolk</p>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Useful Links */}
        <div className="useful-links-section">
          <div className="section-header">
            <h2>Useful Travel Information</h2>
          </div>
          
          <div className="links-grid">
            <a 
              href="https://highwaysengland.co.uk/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-card"
              data-testid="highways-england-link"
            >
              <h3>National Highways</h3>
              <p>Planned roadworks and major route information</p>
            </a>
            
            <a 
              href="https://www.norfolk.gov.uk/roads-and-transport" 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-card"
              data-testid="norfolk-council-link"
            >
              <h3>Norfolk County Council</h3>
              <p>Local road maintenance and diversions</p>
            </a>
            
            <a 
              href="https://www.norfolk.gov.uk/roads-and-transport/roads/report-a-problem" 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-card"
              data-testid="report-problem-link"
            >
              <h3>Report Road Problems</h3>
              <p>Potholes, street lighting, and road defects</p>
            </a>
            
            <a 
              href="https://www.nationalrail.co.uk/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-card"
              data-testid="rail-link"
            >
              <h3>National Rail</h3>
              <p>Train times and rail travel updates</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrafficPage;